import NextLink from 'next/link';

import { Box, Divider, Link, Stack, Text } from '@chakra-ui/react';

import { TypeArea, TypeCity, TypeJobAdvert } from 'generated/contentful';

import { RECRUIT_PATH_PREFIX } from 'common/constants';

import LinkButton from 'common/components/LinkButton/LinkButton';

export interface JobsBoardAdvertProps {
  jobAdvert: TypeJobAdvert<'WITHOUT_UNRESOLVABLE_LINKS', string>;
  area?: TypeArea<'WITHOUT_UNRESOLVABLE_LINKS', string>;
  city?: TypeCity<'WITHOUT_UNRESOLVABLE_LINKS', string>;
}

export default function JobsBoardAdvert({ jobAdvert, area, city }: JobsBoardAdvertProps) {
  if (!city && !area) {
    throw new Error('JobsBoardAdvert with no city or area.');
  }

  const citySlug = area ? area.fields.city?.fields.slug : city!.fields.slug;
  const areaSlug = area ? area.fields.slug : city!.fields.slug;
  const locationName = area ? area.fields.name : city!.fields.name;

  const displayShortDescription = (jobAdvert: TypeJobAdvert<undefined, string>) => {
    return jobAdvert.fields.shortDescription ? <Text>{jobAdvert.fields.shortDescription}</Text> : <></>;
  };

  const displayRate =
    jobAdvert.fields.minimumPay === jobAdvert.fields.maximumPay
      ? `£${jobAdvert.fields.minimumPay.toFixed(2)}`
      : `£${jobAdvert.fields.minimumPay.toFixed(2)} - £${jobAdvert.fields.maximumPay.toFixed(2)}`;

  return (
    <Box mb={8}>
      <Stack direction={['column', 'row']} mb={8} justifyContent="space-between" alignItems={['flex-start', 'center']}>
        <Box maxW={['100%', '60%']}>
          <Text fontWeight="medium" fontSize={['lg', 'xl', '2xl']}>
            <Link
              as={NextLink}
              href={`${RECRUIT_PATH_PREFIX}/jobs/${citySlug}/${areaSlug}/${jobAdvert.fields.slug}/`}
              aria-label={`${jobAdvert.fields.employmentType.join(' or ')} ${
                jobAdvert.fields.title
              } in ${locationName}`}
            >
              {`${jobAdvert.fields.title} in ${locationName}`}
            </Link>
          </Text>
          <Text fontWeight="medium">
            {`${displayRate} Per ${jobAdvert.fields.payFrequency}, ${jobAdvert.fields.employmentType.join(' or ')}`}
          </Text>
          {displayShortDescription(jobAdvert)}
        </Box>
        <Box>
          <LinkButton
            href={`${RECRUIT_PATH_PREFIX}/apply/`}
            size="md"
            mr={4}
            aria-label={`${jobAdvert.fields.employmentType.join(' or ')} ${
              jobAdvert.fields.title
            } in ${locationName} - Apply Now`}
          >
            Apply Now
          </LinkButton>

          <LinkButton
            href={`${RECRUIT_PATH_PREFIX}/jobs/${citySlug}/${areaSlug}/${jobAdvert.fields.slug}/`}
            colorScheme="blue"
            size="md"
            aria-label={`${jobAdvert.fields.employmentType.join(' or ')} ${
              jobAdvert.fields.title
            } in ${locationName} - More Info`}
          >
            More Info
          </LinkButton>
        </Box>
      </Stack>
      <Divider />
    </Box>
  );
}
